import { ArrowLeft, Clock3, Save } from 'lucide-react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Badge, Section, WorkspaceSkeleton } from '../components/UI'
import { ActionToast } from '../components/ActionToast'
import { supabase } from '../lib/supabase'

type Profile = {
  id: string
  organisation_id: string
  team_id: string | null
  name: string
  description: string | null
  is_default: boolean
  is_active: boolean
}

type Rule = {
  id: string
  benchmark_profile_id: string
  metric_key: string
  target_seconds: number
  warning_seconds: number | null
  direction: 'max' | 'min'
}

const orderedMetrics = [
  ['arrival_to_reply_sent_seconds', 'First response', 'Customer email received to first reply sent']
] as const

function formatDuration(seconds: number | null | undefined) {
  if (seconds == null) return 'Not set'
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`
}

export function BenchmarkDetail() {
  const { id } = useParams()
  const [profile, setProfile] = useState<Profile | null>(null)
  const [rules, setRules] = useState<Rule[]>([])
  const [teamName, setTeamName] = useState('Organisation-wide')
  const [role, setRole] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [draftTargets, setDraftTargets] = useState<Record<string, string>>({})

  useEffect(() => {
    async function load() {
      if (!supabase || !id) return

      try {
        const { data: authData, error: authError } = await supabase.auth.getUser()
        if (authError || !authData.user) throw new Error('You are not signed in.')

        const membershipResult = await supabase
          .from('memberships')
          .select('role')
          .eq('user_id', authData.user.id)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle()

        if (membershipResult.error) throw membershipResult.error
        setRole(membershipResult.data?.role ?? '')

        const profileResult = await supabase
          .from('benchmark_profiles')
          .select('id,organisation_id,team_id,name,description,is_default,is_active')
          .eq('id', id)
          .maybeSingle()

        if (profileResult.error) throw profileResult.error
        if (!profileResult.data) return

        const loadedProfile = profileResult.data as Profile
        setProfile(loadedProfile)

        if (loadedProfile.team_id) {
          const teamResult = await supabase
            .from('teams')
            .select('name')
            .eq('id', loadedProfile.team_id)
            .maybeSingle()

          if (!teamResult.error && teamResult.data?.name) setTeamName(teamResult.data.name)
        }

        const rulesResult = await supabase
          .from('benchmark_rules')
          .select('id,benchmark_profile_id,metric_key,target_seconds,warning_seconds,direction')
          .eq('benchmark_profile_id', id)

        if (rulesResult.error) throw rulesResult.error

        const loadedRules = (rulesResult.data ?? []) as Rule[]
        setRules(loadedRules)
        setDraftTargets(
          Object.fromEntries(
            loadedRules.map(rule => [rule.metric_key, String(rule.target_seconds / 60)])
          )
        )
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load SLA target.')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [id])

  const ruleMap = useMemo(
    () => new Map(rules.map(rule => [rule.metric_key, rule])),
    [rules]
  )

  async function save(event: FormEvent) {
    event.preventDefault()
    if (!supabase || !profile || saving) return

    setSaving(true)
    setError('')
    setMessage('')

    try {
      const rows = orderedMetrics.map(([metricKey]) => {
        const minutes = Number(draftTargets[metricKey] ?? '')
        if (!Number.isFinite(minutes) || minutes < 0) {
          throw new Error('Every target must be a valid number of minutes.')
        }

        return {
          benchmark_profile_id: profile.id,
          metric_key: metricKey,
          target_seconds: Math.round(minutes * 60),
          direction: 'max'
        }
      })

      const { error: saveError } = await supabase
        .from('benchmark_rules')
        .upsert(rows, {
          onConflict: 'benchmark_profile_id,metric_key'
        })

      if (saveError) throw saveError

      const rulesResult = await supabase
        .from('benchmark_rules')
        .select('id,benchmark_profile_id,metric_key,target_seconds,warning_seconds,direction')
        .eq('benchmark_profile_id', profile.id)

      if (rulesResult.error) throw rulesResult.error
      setRules((rulesResult.data ?? []) as Rule[])
      setMessage('SLA targets saved.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save SLA target.')
    } finally {
      setSaving(false)
    }
  }

  if (!id) return <Navigate to="/app/sla-performance" replace />
  if (!loading && !profile && !error) return <Navigate to="/app/sla-performance" replace />

  const canManage = role === 'owner' || role === 'admin'

  if (loading) return <WorkspaceSkeleton rows={3}/>

  return (
    <div className="stack-lg detail-page">
      <div className="detail-page-nav">
        <Link to="/app/sla-performance" className="back-link">
          <ArrowLeft size={17}/>
          Back to SLA Performance
        </Link>
      </div>

      {error && (
        <div className="notice">
          <div>
            <strong>SLA target unavailable</strong>
            <span>{error}</span>
          </div>
        </div>
      )}

      {message && <ActionToast title="Saved" message={message} onDismiss={() => setMessage('')}/>}

      {profile && (
        <>
          <section className="detail-page-hero">
            <div>
              <span className="eyebrow">{teamName} SLA target</span>
              <h2>{profile.name}</h2>
              <p>{profile.description || 'Live Mail Percent SLA target'}</p>
            </div>

            <div className="detail-page-badges">
              <Badge tone={profile.is_active ? 'good' : 'neutral'}>
                {profile.is_active ? 'Active' : 'Inactive'}
              </Badge>

              {profile.is_default && <Badge tone="blue">Default</Badge>}
            </div>
          </section>

          <div className="detail-page-stat-grid">
            <div>
              <Clock3 size={20}/>
              <span>First response SLA</span>
              <strong>≤ {formatDuration(ruleMap.get('arrival_to_reply_sent_seconds')?.target_seconds)}</strong>
              <small>Customer email received to first reply sent</small>
            </div>
          </div>

          <Section
            title="Service-level targets"
            subtitle="The SLA thresholds used to interpret measured work"
          >
            <form onSubmit={save} className="benchmark-editor">
              <div className="benchmark-editor-grid">
                {orderedMetrics.map(([metricKey, label, description]) => (
                  <div className="field" key={metricKey}>
                    <label htmlFor={`metric-${metricKey}`}>{label} target (minutes)</label>
                    <input
                      id={`metric-${metricKey}`}
                      type="number"
                      min="0"
                      step="0.5"
                      disabled={!canManage}
                      value={draftTargets[metricKey] ?? ''}
                      onChange={event => setDraftTargets(current => ({
                        ...current,
                        [metricKey]: event.target.value
                      }))}
                    />
                    <small className="muted">{description}</small>
                  </div>
                ))}
              </div>

              {canManage && (
                <div className="page-actions">
                  <button className="button primary" type="submit" disabled={saving}>
                    <Save size={16}/>
                    {saving ? 'Saving…' : 'Save targets'}
                  </button>
                </div>
              )}
            </form>
          </Section>

          <Section
            title="How this SLA is used"
            subtitle="SLA target versus operating conditions"
          >
            <p className="detail-copy">
              Mail Percent compares the measured first-response time with this SLA target.
              Queue pressure, staffing levels and workload context remain separate so the SLA itself is not silently weakened when operating conditions become difficult.
            </p>
          </Section>
        </>
      )}

    </div>
  )
}
